/* ------------------------------------------------------------------
   bioThemes.js — Link-in-Bio theme model (Tier 1 customization)
   ------------------------------------------------------------------
   A theme is a JSON object:
   {
     preset: 'midnight' | 'custom' | ...,
     bg:     { type: 'solid'|'gradient'|'image', color, from, to, angle, imageUrl },
     button: { shape: 'rounded'|'pill'|'square', fill: 'solid'|'outline',
               color, text, shadow: bool },
     text:   '#...'  (main text color),
     sub:    '#...'  (secondary text color),
   }
   Old themes ({preset:'x'} only) still render — resolveTheme() fills the gaps
   from the preset defaults.
------------------------------------------------------------------ */

export const PRESETS = {
  light: {
    name: 'Light',
    bg: { type: 'solid', color: '#f8fafc' },
    button: { shape: 'rounded', fill: 'solid', color: '#111827', text: '#ffffff', shadow: false },
    text: '#111827', sub: '#6b7280',
  },
  midnight: {
    name: 'Midnight',
    bg: { type: 'solid', color: '#0f172a' },
    button: { shape: 'rounded', fill: 'solid', color: '#1e293b', text: '#f8fafc', shadow: false },
    text: '#f8fafc', sub: '#94a3b8',
  },
  ocean: {
    name: 'Ocean',
    bg: { type: 'gradient', from: '#0ea5e9', to: '#2563eb', angle: 135 },
    button: { shape: 'pill', fill: 'solid', color: '#ffffff', text: '#0c4a6e', shadow: false },
    text: '#ffffff', sub: '#e0f2fe',
  },
  sunset: {
    name: 'Sunset',
    bg: { type: 'gradient', from: '#f97316', to: '#db2777', angle: 135 },
    button: { shape: 'pill', fill: 'solid', color: '#ffffff', text: '#7c2d12', shadow: false },
    text: '#ffffff', sub: '#ffe4e6',
  },
  forest: {
    name: 'Forest',
    bg: { type: 'gradient', from: '#22c55e', to: '#15803d', angle: 135 },
    button: { shape: 'rounded', fill: 'solid', color: '#ffffff', text: '#14532d', shadow: false },
    text: '#ffffff', sub: '#dcfce7',
  },
  bold: {
    name: 'Bold',
    bg: { type: 'solid', color: '#fde047' },
    button: { shape: 'square', fill: 'solid', color: '#111827', text: '#fde047', shadow: true },
    text: '#111827', sub: '#374151',
  },
};

/* deep-clone a preset into an editable theme object */
export function themeFromPreset(key) {
  const p = PRESETS[key] || PRESETS.light;
  return {
    preset: key,
    bg: { ...p.bg },
    button: { ...p.button },
    text: p.text,
    sub: p.sub,
  };
}

/* merge a (possibly partial / legacy) theme over its preset defaults */
export function resolveTheme(theme) {
  const base = PRESETS[(theme && theme.preset)] || PRESETS.light;
  return {
    bg: { ...base.bg, ...(theme && theme.bg) },
    button: { ...base.button, ...(theme && theme.button) },
    text: (theme && theme.text) || base.text,
    sub: (theme && theme.sub) || base.sub,
  };
}

/* turn a theme into concrete CSS values for rendering */
export function themeToCss(theme) {
  const t = resolveTheme(theme);

  const radius = { rounded: 12, pill: 999, square: 4 }[t.button.shape] ?? 12;
  const outline = t.button.fill === 'outline';

  return {
    pageBackground: backgroundCss(t.bg),
    textColor: t.text,
    subColor: t.sub,
    button: {
      background: outline ? 'transparent' : t.button.color,
      color: outline ? t.button.color : t.button.text,
      border: `2px solid ${t.button.color}`,
      borderRadius: radius,
      boxShadow: t.button.shadow ? '5px 5px 0 rgba(0,0,0,0.85)' : 'none',
    },
    avatar: { background: t.button.color, color: t.button.text },
    resolved: t,
  };
}

function backgroundCss(bg) {
  if (!bg) return '#f8fafc';
  if (bg.type === 'gradient') return `linear-gradient(${bg.angle ?? 135}deg, ${bg.from || '#2563eb'}, ${bg.to || '#7c3aed'})`;
  if (bg.type === 'image' && bg.imageUrl) return `url("${bg.imageUrl}") center / cover no-repeat`;
  return bg.color || '#f8fafc';
}

/* small CSS string for preset swatch previews */
export function presetSwatchBg(key) {
  return backgroundCss((PRESETS[key] || PRESETS.light).bg);
}
